import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import awsVendorWebSocketService from '../services/awsVendorWebSocketService';

// Styled Components
const MarketplaceContainer = styled.div`
  background-color: #0a0a0a;
  border-radius: 12px;
  padding: 24px;
  color: white;
  min-height: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const MarketplaceHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const MarketplaceTitle = styled.h2`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: bold;
`;

const MarketplaceSubtitle = styled.p`
  color: #ccc;
  margin: 0;
  font-size: 16px;
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  
  background-color: ${props => props.$isConnected ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  color: ${props => props.$isConnected ? '#22c55e' : '#ef4444'};
  border: 1px solid ${props => props.$isConnected ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$isConnected ? '#22c55e' : '#ef4444'};
  animation: ${props => props.$isConnected ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  overflow-x: auto;
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #D4AF37;
    border-radius: 3px;
  }
`;

const CategoryTab = styled.button`
  padding: 10px 20px;
  background-color: ${props => props.$active ? '#D4AF37' : 'transparent'};
  color: ${props => props.$active ? '#0a0a0a' : '#D4AF37'};
  border: 2px solid #D4AF37;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: ${props => props.$active ? 'bold' : '500'};
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.$active ? '#B8860B' : 'rgba(212, 175, 55, 0.1)'};
    transform: translateY(-1px);
  }
`;

const VendorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const RestaurantCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 2px solid ${props => props.$isOnline ? '#22c55e' : '#4b5563'};
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: ${props => props.$isOnline ? '#16a34a' : '#6b7280'};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #D4AF37, #fbbf24);
    opacity: ${props => props.$isOnline ? '1' : '0.3'};
  }
`;

const OnlineIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$isOnline ? '#22c55e' : '#9ca3af'};
`;

const OnlineDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$isOnline ? '#22c55e' : '#9ca3af'};
  animation: ${props => props.$isOnline ? 'pulse 2s infinite' : 'none'};
`;

const RestaurantName = styled.h3`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: bold;
`;

const RestaurantDescription = styled.p`
  color: #d1d5db;
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.4;
`;

const RestaurantSpecialties = styled.div`
  margin-bottom: 12px;
`;

const SpecialtiesTitle = styled.div`
  color: #9ca3af;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SpecialtiesList = styled.div`
  color: #d1d5db;
  font-size: 13px;
  line-height: 1.3;
`;

const RestaurantLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  color: #9ca3af;
  font-size: 13px;
`;

const LocationIcon = styled.span`
  font-size: 14px;
`;

const RestaurantStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #9ca3af;
`;

const MenuItemCount = styled.div`
  color: #D4AF37;
  font-weight: 500;
`;

const LastUpdated = styled.div`
  color: #6b7280;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #D4AF37;
  font-size: 18px;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyStateText = styled.p`
  color: #9ca3af;
  font-size: 16px;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const ActionButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #D4AF37 0%, #fbbf24 100%);
  color: #0a0a0a;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(212, 175, 55, 0.3);
  }
`;

const CloseButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: #9ca3af;
  border: 2px solid #4b5563;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #6b7280;
    color: #d1d5db;
  }
`;

const RestaurantMarketplace = ({ onRestaurantSelect, onClose }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: false,
    mode: 'unknown',
    error: null
  });

  // Categories for filtering
  const categories = [
    { id: 'all', name: 'All Restaurants', emoji: '🍽️' },
    { id: 'food', name: 'Food Trucks', emoji: '🌮' },
    { id: 'drinks', name: 'Drink Stands', emoji: '🥤' },
    { id: 'desserts', name: 'Desserts', emoji: '🍰' },
    { id: 'coffee', name: 'Coffee & Tea', emoji: '☕' }
  ];

  useEffect(() => {
    initializeMarketplace();
    return () => {
      // Cleanup WebSocket listeners
      if (awsVendorWebSocketService) {
        awsVendorWebSocketService.disconnect();
      }
    };
  }, []);

  const initializeMarketplace = async () => {
    try {
      console.log('🚀 Initializing restaurant marketplace...');
      
      // Connect to AWS WebSocket for real-time updates
      await connectToWebSocket();
      
      // Fetch initial restaurant data
      await fetchRestaurants();
      
    } catch (error) {
      console.error('❌ Failed to initialize marketplace:', error);
      setConnectionStatus({
        isConnected: false,
        mode: 'offline',
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const connectToWebSocket = async () => {
    try {
      console.log('🔌 Connecting to AWS WebSocket...');
      
      // Subscribe to vendor events
      awsVendorWebSocketService.subscribe(handleVendorEvent);
      
      // Connect to WebSocket
      await awsVendorWebSocketService.connect();
      
      setConnectionStatus({
        isConnected: true,
        mode: 'websocket',
        error: null
      });
      
      console.log('✅ WebSocket connected successfully');
      
    } catch (error) {
      console.error('❌ WebSocket connection failed:', error);
      throw error;
    }
  };

  const fetchRestaurants = async () => {
    try {
      console.log('📡 Fetching restaurants from Codebase 1...');
      
      // This would call your Codebase 1 API endpoint
      // For now, using mock data that matches the expected format
      const mockRestaurants = [
        {
          merchantId: 'MLQ0SBX0ECQ87',
          businessName: 'Joe\'s Food Truck',
          status: 'active',
          posSystem: 'square',
          description: 'Delicious street food and gourmet burgers',
          specialties: ['Burgers', 'Tacos', 'Fries'],
          address: 'Main Festival Grounds',
          lastUpdated: new Date().toISOString(),
          category: 'food'
        },
        {
          merchantId: 'DEMO001',
          businessName: 'Smoothie Paradise',
          status: 'active',
          posSystem: 'square',
          description: 'Fresh fruit smoothies and healthy drinks',
          specialties: ['Smoothies', 'Juices', 'Acai Bowls'],
          address: 'Food Court Area',
          lastUpdated: new Date().toISOString(),
          category: 'drinks'
        }
      ];
      
      setRestaurants(mockRestaurants);
      console.log('✅ Restaurants loaded:', mockRestaurants.length);
      
    } catch (error) {
      console.error('❌ Failed to fetch restaurants:', error);
      // Fallback to empty state
      setRestaurants([]);
    }
  };

  const handleVendorEvent = (event) => {
    console.log('📨 Received vendor event:', event);
    
    switch (event.type) {
      case 'VENDOR_ADDED':
        console.log('🆕 New restaurant added:', event.vendor);
        setRestaurants(prev => [...prev, {
          ...event.vendor,
          category: 'food', // Default category
          lastUpdated: new Date().toISOString()
        }]);
        break;
        
      case 'VENDOR_STATUS_CHANGED':
        console.log('🔄 Restaurant status changed:', event.vendor);
        setRestaurants(prev => prev.map(restaurant => 
          restaurant.merchantId === event.vendor.merchantId
            ? { ...restaurant, ...event.vendor, lastUpdated: new Date().toISOString() }
            : restaurant
        ));
        break;
        
      case 'VENDOR_MENU_UPDATED':
        console.log('📋 Restaurant menu updated:', event.vendor);
        setRestaurants(prev => prev.map(restaurant => 
          restaurant.merchantId === event.vendor.merchantId
            ? { ...restaurant, menu: event.vendor.menu, lastUpdated: new Date().toISOString() }
            : restaurant
        ));
        break;
        
      default:
        console.log('📨 Unknown event type:', event.type);
    }
  };

  const getFilteredRestaurants = () => {
    if (selectedCategory === 'all') {
      return restaurants.filter(restaurant => restaurant.status === 'active');
    }
    return restaurants.filter(restaurant => 
      restaurant.status === 'active' && restaurant.category === selectedCategory
    );
  };

  const handleRestaurantClick = (restaurant) => {
    if (restaurant.status === 'active') {
      onRestaurantSelect(restaurant);
    }
  };

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  const filteredRestaurants = getFilteredRestaurants();

  return (
    <MarketplaceContainer>
      <MarketplaceHeader>
        <MarketplaceTitle>🍽️ Live Restaurant Marketplace</MarketplaceTitle>
        <MarketplaceSubtitle>
          Discover amazing food trucks and restaurants available now!
        </MarketplaceSubtitle>
      </MarketplaceHeader>

      <ConnectionStatus $isConnected={connectionStatus.isConnected}>
        <StatusDot $isConnected={connectionStatus.isConnected} />
        {connectionStatus.isConnected 
          ? `Live Updates Active (${connectionStatus.mode || 'unknown'})`
          : connectionStatus.error 
            ? `Connection Error: ${connectionStatus.error}`
            : 'Connecting...'
        }
      </ConnectionStatus>

      <CategoryTabs>
        {categories.map(category => (
          <CategoryTab
            key={category.id}
            $active={selectedCategory === category.id}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.emoji} {category.name}
          </CategoryTab>
        ))}
      </CategoryTabs>

      {loading ? (
        <LoadingSpinner>
          🔄 Loading live restaurants...
        </LoadingSpinner>
      ) : filteredRestaurants.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>🍽️</EmptyStateIcon>
          <EmptyStateText>
            {selectedCategory === 'all' 
              ? 'No restaurants are currently online. Check back soon!'
              : `No ${categories.find(c => c.id === selectedCategory)?.name.toLowerCase()} are currently available.`
            }
          </EmptyStateText>
        </EmptyState>
      ) : (
        <VendorsGrid>
          {filteredRestaurants.map(restaurant => (
            <RestaurantCard
              key={restaurant.merchantId}
              $isOnline={restaurant.status === 'active'}
              onClick={() => handleRestaurantClick(restaurant)}
            >
              <OnlineIndicator $isOnline={restaurant.status === 'active'}>
                <OnlineDot $isOnline={restaurant.status === 'active'} />
                {restaurant.status === 'active' ? 'Active' : 'Inactive'}
              </OnlineIndicator>
              
              <RestaurantName>{restaurant.businessName}</RestaurantName>
              
              <RestaurantDescription>
                {restaurant.description || 'Delicious food and drinks available now!'}
              </RestaurantDescription>
              
              {restaurant.specialties && (
                <RestaurantSpecialties>
                  <SpecialtiesTitle>Specialties:</SpecialtiesTitle>
                  <SpecialtiesList>
                    {restaurant.specialties.join(' • ')}
                  </SpecialtiesList>
                </RestaurantSpecialties>
              )}
              
              <RestaurantLocation>
                <LocationIcon>📍</LocationIcon>
                {restaurant.address || 'Festival Location'}
              </RestaurantLocation>
              
              <RestaurantStats>
                <MenuItemCount>
                  {restaurant.menu ? `${restaurant.menu.length} items` : 'Menu loading...'}
                </MenuItemCount>
                <LastUpdated>
                  Updated: {formatLastUpdated(restaurant.lastUpdated || restaurant.connectedAt)}
                </LastUpdated>
              </RestaurantStats>
            </RestaurantCard>
          ))}
        </VendorsGrid>
      )}

      <ActionButtons>
        <CloseButton onClick={onClose}>
          Close Marketplace
        </CloseButton>
        <ActionButton onClick={() => onRestaurantSelect({ action: 'show_full_menu' })}>
          View Full Festival Menu
        </ActionButton>
      </ActionButtons>
    </MarketplaceContainer>
  );
};

export default RestaurantMarketplace;
